import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';

/**
 * Mapeamento de rotas para menus
 * 
 * ⚠️ IMPORTANTE: Sempre que uma nova tela/rota for criada, atualize este mapeamento!
 * Este mapeamento deve estar sincronizado com:
 * - backend/app/Services/ResourceDiscoveryService.php (ROUTE_TO_MENU_MAP)
 * - frontend/src/App.js (rotas definidas)
 * 
 * Última atualização: 2025-12-10
 */
const routeToMenuMap = {
  // Dashboard
  '/': 'dashboard',
  '/dashboard': 'dashboard',

  // Billing (Faturamento)
  '/billing': 'billing',
  '/payment-status': 'billing',
  '/card-payment': 'billing',

  // Bot (Gerenciamento de Bots) - todas as rotas relacionadas
  '/bot': 'bot',
  '/bot/list': 'bot',
  '/bot-list': 'bot',
  '/bot/create': 'bot',
  '/create-bot': 'bot',
  '/bot/update': 'bot',
  '/bot/manage': 'bot',
  '/manage-bot': 'bot',
  '/bot/welcome': 'bot',
  '/bot/payment-plans': 'bot',
  '/bot/redirect': 'bot',
  '/bot/administrators': 'bot',
  '/bot/groups': 'bot',
  '/bot/telegram-groups': 'bot',
  '/bot/commands': 'bot',
  '/bot/botfather': 'bot',
  '/bot/group-management': 'bot',
  '/bot/': 'bot', // Wildcard para capturar todas as rotas /bot/*
  '/group-management': 'bot',

  // Results (Resultados)
  '/results': 'results',
  '/results/contacts': 'results',
  '/contact-details': 'results',

  // Marketing
  '/marketing': 'marketing',
  '/marketing/alerts': 'marketing',
  '/marketing/downsell': 'marketing',

  // Settings (Configurações)
  '/settings': 'settings',
  '/settings/payment-cycles': 'settings',
  '/settings/payment-gateways': 'settings',
  '/settings/profile': 'settings',
  '/settings/security': 'settings',
  '/settings/storage': 'settings',
  '/settings/artisan': 'settings',
  '/settings/artisan-commands': 'settings',
  '/settings/cron-jobs': 'settings',
  '/settings/laravel-logs': 'settings',
  '/settings/warning-messages': 'settings',
  '/settings/translations': 'settings',
  '/settings/users': 'settings',
  '/settings/user-groups': 'settings',
  '/settings/logs': 'settings',
  '/settings/ftp-manager': 'settings',
  '/users': 'settings',
  '/user-groups': 'settings',
  '/logs': 'settings',
  '/ftp': 'settings',
};

/**
 * Obtém o nome do menu baseado no path
 */
const getMenuFromPath = (pathname) => {
  // Remove query strings do path
  const pathWithoutQuery = pathname.split('?')[0];
  
  // Remove IDs numéricos do path para normalizar rotas dinâmicas
  const normalizedPath = pathWithoutQuery.replace(/\/\d+/g, '');
  
  // Verifica primeiro por correspondência exata
  if (routeToMenuMap[normalizedPath]) {
    return routeToMenuMap[normalizedPath];
  }
  
  // Verifica se começa com algum prefixo conhecido (ordem específica primeiro)
  if (normalizedPath.startsWith('/bot/update') || normalizedPath === '/bot/update') {
    return 'bot';
  }
  if (normalizedPath.startsWith('/bot/') || normalizedPath.startsWith('/bot-list') || normalizedPath.startsWith('/create-bot')) {
    return 'bot';
  }
  if (normalizedPath.startsWith('/manage-bot')) {
    return 'bot';
  }
  if (normalizedPath.startsWith('/group-management')) {
    return 'bot';
  }
  if (normalizedPath.startsWith('/results/') || normalizedPath.startsWith('/contact-details')) {
    return 'results';
  }
  if (normalizedPath.startsWith('/marketing/') || normalizedPath === '/marketing') {
    return 'marketing';
  }
  if (normalizedPath.startsWith('/payment-status') || normalizedPath.startsWith('/card-payment')) {
    return 'billing';
  }
  if (normalizedPath.startsWith('/billing')) {
    return 'billing';
  }
  if (normalizedPath.startsWith('/settings/')) {
    return 'settings';
  }
  
  // Verifica correspondência exata novamente (caso tenha removido algo importante)
  if (routeToMenuMap[pathWithoutQuery]) {
    return routeToMenuMap[pathWithoutQuery];
  }
  
  return null;
};

/**
 * Componente de rota protegida que verifica permissões de menu
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Super admin tem acesso a tudo
  if (user?.user_type === 'super_admin') {
    return children;
  }

  // Se não tem grupo, não tem acesso (exceto se for super admin, já tratado acima)
  if (!user?.user_group_id) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Acesso Negado</h1>
        <p>Você não tem permissão para acessar esta página.</p>
      </div>
    );
  }

  // Rotas que todos os usuários autenticados podem acessar (não precisam de permissão de menu)
  const publicRoutes = ['/settings/profile'];
  
  // Se a rota é pública, permite acesso
  if (publicRoutes.includes(location.pathname)) {
    return children;
  }

  // Obtém o menu correspondente à rota atual
  const menuName = getMenuFromPath(location.pathname);

  // Se não encontrou o menu, permite acesso (pode ser uma rota que não precisa de permissão)
  if (!menuName) {
    return children;
  }

  // Verifica se o usuário tem acesso ao menu
  const accessibleMenus = user?.accessible_menus || [];
  
  // Se tem acesso a todos os menus (*) ou ao menu específico
  if (accessibleMenus.includes('*') || accessibleMenus.includes(menuName)) {
    return children;
  }

  // Não tem permissão
  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h1>Acesso Negado</h1>
      <p>Você não tem permissão para acessar esta página.</p>
    </div>
  );
};

export default ProtectedRoute;

